import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import EmailVerificationForm from '../../../components/PasswordReset/EmailVerificationForm';
import apiClient from '../../../apiClient';
import './ForgotPasswordPage.css';

function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await apiClient.post('/api/password/request-reset', {
        email: email.toLowerCase().trim()
      });

      if (response.data.success) {
        setIsSuccess(true);
        setMessage(response.data.message || 'If an account exists with this email, a password reset link has been sent.');
        setAttempts(0);
      } else {
        setError(response.data.message || 'An error occurred. Please try again.');
        setAttempts(prev => prev + 1);
      }
    } catch (err) {
      if (err.response?.status === 429) {
        setError('Too many attempts. Please try again later.');
      } else if (err.response?.status === 400) {
        setError(err.response.data.message || 'Invalid email address.');
      } else {
        setError('An error occurred. Please try again later.');
      }
      setAttempts(prev => prev + 1);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await apiClient.post('/api/password/resend-reset', {
        email: email.toLowerCase().trim()
      });

      if (response.data.success) {
        setMessage('Password reset email has been resent.');
      } else {
        setError(response.data.message || 'Unable to resend email.');
      }
    } catch (err) {
      setError('Unable to resend email. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  // Function to programmatically navigate back to login
  const handleBackToLogin = () => {
    navigate('/');
  };

  // Optional: Auto-redirect to login after 5 minutes on success
  React.useEffect(() => {
    if (isSuccess) {
      const timer = setTimeout(() => {
        navigate('/');
      }, 300000); // 5 minutes

      return () => clearTimeout(timer);
    }
  }, [isSuccess, navigate]);

  return (
    <div className="forgot-password-page">
      <div className="forgot-password-container">
        {!isSuccess ? (
          <>
            <div className="forgot-password-header">
              <h2>Reset Your Password</h2>
              <p>Enter your email address and we'll send you a link to reset your password.</p>
            </div>

            <EmailVerificationForm
              email={email}
              onEmailChange={setEmail}
              onSubmit={handleSubmit}
              isLoading={isLoading}
              error={error}
              attempts={attempts}
            />

            <div className="forgot-password-footer">
              <Link to="/" className="back-to-login">
                ← Back to Login
              </Link>
            </div>
          </>
        ) : (
          <div className="success-container">
            <div className="success-icon">✓</div>
            <h2>Check Your Email</h2>
            <p className="success-message">{message}</p>

            <div className="success-details">
              <p>We've sent a password reset link to:</p>
              <p className="email-display">{email}</p>
            </div>

            <div className="success-instructions">
              <h3>Next Steps:</h3>
              <ol>
                <li>Check your email inbox</li>
                <li>Click the reset link in the email</li>
                <li>Create a new secure password</li>
                <li>The link will expire in 30 minutes for security reasons. If you don't see the email, check your spam folder.</li>
              </ol>
            </div>
            <div className="success-actions">
              <button
                onClick={handleResend}
                disabled={isLoading}
                className="resend-button"
              >
                {isLoading ? 'Resending...' : 'Resend Email'}
              </button>

              <button
                onClick={handleBackToLogin}
                className="back-to-login-button"
              >
                Back to Login
              </button>
            </div>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ForgotPasswordPage;